import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { wsManager } from "../services/WebSocketData";
import { getOrderHistory } from "../services/api";
import "../styles/OrderHistory.css";

const ITEMS_PER_PAGE = 10;
const FETCH_THROTTLE_MS = 10000;
const MAX_HISTORY_ORDERS = 300;

const DAY_MS = 24 * 60 * 60 * 1000;

const toISODateInput = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const endOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
};

const parseDateInput = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const FILTER_CONFIG = [
  { key: "all", label: "📋 Todas" },
  { key: "win", label: "✅ Ganadoras" },
  { key: "loss", label: "❌ Perdedoras" },
];

const normalizeStatus = (statusRaw) => {
  const status = String(statusRaw || "OPEN").toUpperCase().trim();
  if (status === "CLOSED" || status === "CERRADA") return "CLOSED";
  return "OPEN";
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickPositiveNumber = (...values) => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric !== null && numeric > 0) {
      return numeric;
    }
  }
  return null;
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const str = String(value);
  const timestamp = new Date(str.endsWith("Z") ? str : str + "Z").getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const formatCurrency = (value, digits = 4, signed = false) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = signed && numeric > 0 ? "+" : "";
  return `${sign}$${numeric.toFixed(digits)}`;
};

const formatPercent = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(digits)}%`;
};

const formatQuantity = (value, digits = 6) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  return numeric.toFixed(digits);
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const str = String(value);
  const date = new Date(str.endsWith("Z") ? str : str + "Z");
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDurationFallback = (start, end) => {
  if (!start || !end) return "N/A";
  const startTs = toTimestamp(start);
  const endTs = toTimestamp(end);
  if (!startTs || !endTs || endTs < startTs) return "N/A";

  const totalMinutes = Math.round((endTs - startTs) / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const buildOrderKey = (order, index) => {
  const id = String(order?.orderId || "").trim();
  const symbol = String(order?.symbol || "N/A").trim();
  const sellDate = String(order?.fecha_venta || order?.sell_timestamp || "").trim();
  const buyDate = String(order?.fecha_compra || order?.timestamp || "").trim();
  const timePart = sellDate || buyDate || index;

  if (id) return `id:${id}:${timePart}:${index}`;

  return `row:${symbol}:${timePart}:${index}`;
};

const getSaleReasonMeta = (reasonRaw) => {
  const reason = String(reasonRaw || "MANUAL").toUpperCase().trim();

  if (reason.includes("TP2")) return { label: "🟢 TP2", tone: "positive" };
  if (reason.includes("TP1")) return { label: "🟡 TP1", tone: "warning" };
  if (reason.includes("TAKE_PROFIT") || reason.includes("TP")) {
    return { label: "🎯 TAKE PROFIT", tone: "positive" };
  }
  if (reason.includes("TRAILING")) return { label: "📊 TRAILING", tone: "negative" };
  if (reason.includes("STOPLOSS") || reason.includes("SL")) {
    return { label: "🛑 STOP LOSS", tone: "negative" };
  }
  if (reason.includes("MANUAL")) return { label: "👤 MANUAL", tone: "neutral" };

  return { label: reason, tone: "neutral" };
};

const getExecutionModeMeta = (order) => {
  const mode = String(
    order?.execution_mode || order?.trade_mode || (order?.is_simulation ? "SIMULATION" : "REAL")
  )
    .toUpperCase()
    .trim();
  if (mode.includes("SIM")) return { label: "SIM", tone: "warning" };
  return { label: "REAL", tone: "positive" };
};

const getEntryMethodMeta = (order) => {
  const method = String(order?.entry_method || order?._entry_strategy || "").trim();
  if (!method) return null;
  const m = method.toUpperCase();
  let tone = "info";
  if (m.includes("EXPRESS") || m.includes("ULTRA")) tone = "positive";
  else if (m.includes("REBOTE") || m.includes("NY")) tone = "warning";
  else if (m.includes("IMPULSO")) tone = "neutral";
  return { label: method, tone };
};

const getMarketModeMeta = (order) => {
  const code = String(order?.market_mode || "").trim().toLowerCase();
  const label = String(order?.market_mode_label || "").trim();
  const detail = String(order?.market_mode_detail || "").trim();
  const tone = String(order?.market_mode_tone || "").trim();

  if (label) {
    return { label, tone: tone || "neutral", detail };
  }
  if (code === "range_intraday") {
    return { label: "Rango intradía", tone: "warning", detail };
  }
  if (code === "rebound_momentum") {
    return { label: "Rebote / momentum", tone: "neutral", detail };
  }
  if (code === "momentum_continuation") {
    return { label: "Continuación / momentum", tone: "positive", detail };
  }
  return null;
};

const calculateStats = (orders) => {
  if (!Array.isArray(orders) || orders.length === 0) {
    return {
      totalClosed: 0,
      winCount: 0,
      lossCount: 0,
      winRate: 0,
      grossProfit: 0,
      grossLoss: 0,
      netPnL: 0,
      modeStats: [],
    };
  }

  let winCount = 0;
  let lossCount = 0;
  let grossProfit = 0;
  let grossLoss = 0;

  orders.forEach((order) => {
    const pnl = toNumber(order?.p_l) ?? 0;
    if (pnl > 0) {
      winCount += 1;
      grossProfit += pnl;
    } else if (pnl < 0) {
      lossCount += 1;
      grossLoss += Math.abs(pnl);
    }
  });

  const totalClosed = winCount + lossCount;
  const winRate = totalClosed > 0 ? (winCount / totalClosed) * 100 : 0;
  const netPnL = grossProfit - grossLoss;
  const modeMap = new Map();

  orders.forEach((order) => {
    const meta = getMarketModeMeta(order);
    const key = meta?.label || "Sin modo";
    const current = modeMap.get(key) || {
      label: key,
      tone: meta?.tone || "neutral",
      count: 0,
      netPnL: 0,
    };
    current.count += 1;
    current.netPnL += toNumber(order?.p_l) ?? 0;
    modeMap.set(key, current);
  });

  const modeStats = Array.from(modeMap.values()).sort((a, b) => b.count - a.count);

  return {
    totalClosed,
    winCount,
    lossCount,
    winRate,
    grossProfit,
    grossLoss,
    netPnL,
    modeStats,
  };
};

const buildPageNumbers = (currentPage, totalPages, maxButtons = 5) => {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, currentPage - half);
  let end = Math.min(totalPages, start + maxButtons - 1);

  if (end - start + 1 < maxButtons) {
    start = Math.max(1, end - maxButtons + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const OrderHistory = ({
  refreshTrigger = 0,
  manualRefreshTrigger = 0,
  symbol = "DASH_USDT",
}) => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [fromDate, setFromDate] = useState(() =>
    toISODateInput(new Date(Date.now() - 30 * DAY_MS))
  );
  const [toDate, setToDate] = useState(() => toISODateInput(new Date()));
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedOrderKey, setExpandedOrderKey] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const lastFetchRef = useRef(0);

  const requestedSymbol = useMemo(() => String(symbol || "").trim(), [symbol]);

  const fetchOrders = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) setLoading(true);

    try {
      const response = await getOrderHistory(requestedSymbol);

      let orders = [];
      if (Array.isArray(response)) {
        orders = response;
      } else if (response && Array.isArray(response.orders)) {
        orders = response.orders;
      } else if (response && Array.isArray(response.data)) {
        orders = response.data;
      }

      setAllOrders(Array.isArray(orders) ? orders.slice(0, MAX_HISTORY_ORDERS) : []);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("❌ Error cargando historial de órdenes:", err);
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo cargar el historial";
      setError(message);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [requestedSymbol]);

  const fetchOrdersThrottled = useCallback(
    ({ showLoader = false } = {}) => {
      if (typeof document !== "undefined" && document.hidden) return;
      const now = Date.now();
      if (now - lastFetchRef.current < FETCH_THROTTLE_MS) return;
      lastFetchRef.current = now;
      fetchOrders({ showLoader });
    },
    [fetchOrders]
  );

  useEffect(() => {
    fetchOrders({ showLoader: true });

    const unsubscribe = wsManager.subscribe((data) => {
      if (
        data?.type === "order_update" ||
        data?.type === "compra" ||
        data?.type === "venta"
      ) {
        fetchOrdersThrottled({ showLoader: false });
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [fetchOrders, fetchOrdersThrottled]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchOrdersThrottled({ showLoader: false });
    }
  }, [refreshTrigger, fetchOrdersThrottled]);

  useEffect(() => {
    if (manualRefreshTrigger > 0) {
      lastFetchRef.current = Date.now();
      fetchOrders({ showLoader: false });
    }
  }, [manualRefreshTrigger, fetchOrders]);

  const closedOrders = useMemo(() => {
    return (Array.isArray(allOrders) ? allOrders : [])
      .filter((order) => normalizeStatus(order?.status) === "CLOSED")
      .map((order, index) => ({
        ...order,
        __rowKey: buildOrderKey(order, index),
      }))
      .sort((a, b) => {
        const sellA = toTimestamp(a?.fecha_venta || a?.sell_timestamp);
        const sellB = toTimestamp(b?.fecha_venta || b?.sell_timestamp);
        return sellB - sellA;
      });
  }, [allOrders]);

  const stats = useMemo(() => {
    const from = fromDate ? parseDateInput(fromDate) : null;
    const to = toDate ? parseDateInput(toDate) : null;
    const fromTs = from ? startOfDay(from).getTime() : null;
    const toTs = to ? endOfDay(to).getTime() : null;

    const withinRange = (order) => {
      const timestamp = toTimestamp(order?.fecha_venta || order?.sell_timestamp);
      if (!timestamp) return true;
      if (fromTs !== null && timestamp < fromTs) return false;
      if (toTs !== null && timestamp > toTs) return false;
      return true;
    };

    return calculateStats(closedOrders.filter(withinRange));
  }, [closedOrders, fromDate, toDate]);

  const filteredOrders = useMemo(() => {
    const from = fromDate ? parseDateInput(fromDate) : null;
    const to = toDate ? parseDateInput(toDate) : null;
    const fromTs = from ? startOfDay(from).getTime() : null;
    const toTs = to ? endOfDay(to).getTime() : null;

    const withinRange = (order) => {
      const timestamp = toTimestamp(order?.fecha_venta || order?.sell_timestamp);
      if (!timestamp) return true;
      if (fromTs !== null && timestamp < fromTs) return false;
      if (toTs !== null && timestamp > toTs) return false;
      return true;
    };

    const dateFiltered = closedOrders.filter(withinRange);

    if (filter === "all") return dateFiltered;

    return dateFiltered.filter((order) => {
      const pnl = toNumber(order?.p_l) ?? 0;
      if (filter === "win") return pnl > 0;
      if (filter === "loss") return pnl < 0;
      return true;
    });
  }, [closedOrders, filter, fromDate, toDate]);

  const filterCounts = useMemo(() => {
    const from = fromDate ? parseDateInput(fromDate) : null;
    const to = toDate ? parseDateInput(toDate) : null;
    const fromTs = from ? startOfDay(from).getTime() : null;
    const toTs = to ? endOfDay(to).getTime() : null;

    const inRange = closedOrders.filter((order) => {
      const timestamp = toTimestamp(order?.fecha_venta || order?.sell_timestamp);
      if (!timestamp) return true;
      if (fromTs !== null && timestamp < fromTs) return false;
      if (toTs !== null && timestamp > toTs) return false;
      return true;
    });

    let win = 0;
    let loss = 0;

    inRange.forEach((order) => {
      const pnl = toNumber(order?.p_l) ?? 0;
      if (pnl > 0) win += 1;
      if (pnl < 0) loss += 1;
    });

    return {
      all: inRange.length,
      win,
      loss,
    };
  }, [closedOrders, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  const viewRows = useMemo(() => {
    return paginatedOrders.map((order) => {
      const rowKey = order.__rowKey;
      const isExpanded = expandedOrderKey === rowKey;

      const buyPrice = pickPositiveNumber(order?.precio_compra, order?.buy_price);
      const sellPrice = pickPositiveNumber(order?.precio_venta, order?.sell_price);
      const quantity = pickPositiveNumber(order?.cantidad, order?.size);
      const buyValue =
        order?.valor_compra != null
          ? pickPositiveNumber(order?.valor_compra)
          : buyPrice !== null && quantity !== null
          ? buyPrice * quantity
          : null;
      const sellValue =
        order?.valor_venta != null
          ? pickPositiveNumber(order?.valor_venta)
          : sellPrice !== null && quantity !== null
          ? sellPrice * quantity
          : null;
      const sellNetValue = pickPositiveNumber(order?.valor_neto_venta, sellValue);
      const sellCommission = toNumber(order?.comision_venta) ?? 0;
      const pnl = toNumber(order?.p_l) ?? 0;
      const pnlPercent = toNumber(order?.p_l_porcentaje) ?? 0;
      const maxPrice = pickPositiveNumber(order?.max_precio, order?.max_price_since_buy, sellPrice);

      const slPrice = pickPositiveNumber(order?.sl_price, order?.sl);
      const tpPrice = pickPositiveNumber(order?.tp_price, order?.tp);

      const slPercentFromPrices =
        buyPrice !== null && slPrice !== null && buyPrice > 0 && slPrice < buyPrice
          ? ((buyPrice - slPrice) / buyPrice) * 100
          : null;
      const tpPercentFromPrices =
        buyPrice !== null && tpPrice !== null && buyPrice > 0 && tpPrice > buyPrice
          ? ((tpPrice - buyPrice) / buyPrice) * 100
          : null;

      const slPercent = pickPositiveNumber(order?.sl_percentage, slPercentFromPrices);
      const tpPercent = pickPositiveNumber(order?.tp_percentage, tpPercentFromPrices);

      const rrRatioFromPrices =
        buyPrice !== null &&
        tpPrice !== null &&
        slPrice !== null &&
        tpPrice > buyPrice &&
        slPrice < buyPrice
          ? (tpPrice - buyPrice) / (buyPrice - slPrice)
          : null;
      const rrRatio = pickPositiveNumber(order?.rr_ratio, rrRatioFromPrices);

      const durationRaw = String(order?.duracion || "").trim();
      const duration =
        durationRaw && durationRaw.toUpperCase() !== "N/A"
          ? durationRaw
          : formatDurationFallback(order?.fecha_compra, order?.fecha_venta);

      const sellPartialSize = pickPositiveNumber(order?.sell_partial_size);
      const tp1Executed =
        Boolean(order?.tp1_ejecutado) || String(order?.razon_venta || "").toUpperCase().includes("TP1");
      const slMethod = String(order?.sl_method || "").trim() || "N/A";
      const tpMethod = String(order?.tp_method || "").trim() || "N/A";

      const maxPriceReached = pickPositiveNumber(
        order?.max_price_since_buy,
        order?.max_precio,
        sellPrice,
        buyPrice
      );
      const maxPotentialPct =
        buyPrice !== null && maxPriceReached !== null && buyPrice > 0
          ? ((maxPriceReached - buyPrice) / buyPrice) * 100
          : null;

      const saleReason = getSaleReasonMeta(order?.razon_venta);
      const executionMode = getExecutionModeMeta(order);
      const entryMethod = getEntryMethodMeta(order);
      const marketMode = getMarketModeMeta(order);
      const rowTone = pnl > 0 ? "win" : pnl < 0 ? "loss" : "flat";

      return {
        order,
        rowKey,
        isExpanded,
        buyPrice,
        sellPrice,
        quantity,
        buyValue,
        sellValue,
        sellNetValue,
        sellCommission,
        pnl,
        pnlPercent,
        maxPrice,
        slPrice,
        tpPrice,
        slPercent,
        tpPercent,
        rrRatio,
        duration,
        sellPartialSize,
        tp1Executed,
        slMethod,
        tpMethod,
        maxPriceReached,
        maxPotentialPct,
        saleReason,
        executionMode,
        entryMethod,
        marketMode,
        rowTone,
      };
    });
  }, [paginatedOrders, expandedOrderKey]);

  useEffect(() => {
    if (!expandedOrderKey) return;
    const existsOnPage = paginatedOrders.some((order) => order.__rowKey === expandedOrderKey);
    if (!existsOnPage) setExpandedOrderKey(null);
  }, [expandedOrderKey, paginatedOrders]);

  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages, 5),
    [currentPage, totalPages]
  );

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    setExpandedOrderKey(null);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
    setExpandedOrderKey(null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
    setExpandedOrderKey(null);
  };

  const applyLastMonth = () => {
    setFromDate(toISODateInput(new Date(Date.now() - 30 * DAY_MS)));
    setToDate(toISODateInput(new Date()));
    setCurrentPage(1);
    setExpandedOrderKey(null);
  };

  const clearDateRange = () => {
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setExpandedOrderKey(null);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setExpandedOrderKey(null);
  };

  const selectedSymbol = String(symbol || "N/A").toUpperCase();

  if (loading && closedOrders.length === 0) {
    return (
      <div className="order-history history-loading">
        <div className="history-spinner" />
        <p>⏳ Cargando historial...</p>
      </div>
    );
  }

  return (
    <div className="order-history">
      <header className="history-header">
        <div>
          <h2 className="history-title">📋 Historial de Órdenes</h2>
          <p className="history-subtitle">Análisis de resultados y trazabilidad de cierres</p>
        </div>

        <div className="history-meta">
          <span>🧿 Símbolo activo: {selectedSymbol}</span>
          <span>🕒 {lastUpdate ? `Actualizado ${lastUpdate.toLocaleTimeString()}` : "Sin actualización"}</span>
          <span>
            📄 Pág {currentPage}/{totalPages}
          </span>
        </div>
      </header>

      {error && <div className="history-error">❌ {error}</div>}

      <section className="history-stats-grid">
        <article className="history-stat-card">
          <span className="history-stat-label">📊 Cerradas</span>
          <strong className="history-stat-value">{stats.totalClosed}</strong>
        </article>

        <article className="history-stat-card tone-positive">
          <span className="history-stat-label">✅ Ganadoras</span>
          <strong className="history-stat-value">{stats.winCount}</strong>
          <small className="history-stat-foot">{formatCurrency(stats.grossProfit, 2)}</small>
        </article>

        <article className="history-stat-card tone-negative">
          <span className="history-stat-label">❌ Perdedoras</span>
          <strong className="history-stat-value">{stats.lossCount}</strong>
          <small className="history-stat-foot">-{formatCurrency(stats.grossLoss, 2)}</small>
        </article>

        <article className="history-stat-card">
          <span className="history-stat-label">📈 Win Rate</span>
          <strong className="history-stat-value tone-positive">{stats.winRate.toFixed(2)}%</strong>
        </article>

        <article
          className={`history-stat-card ${stats.netPnL >= 0 ? "tone-positive" : "tone-negative"}`}
        >
          <span className="history-stat-label">🎯 PnL Neto</span>
          <strong className={`history-stat-value ${stats.netPnL >= 0 ? "tone-positive" : "tone-negative"}`}>
            {formatCurrency(stats.netPnL, 2, true)}
          </strong>
        </article>

        {stats.modeStats.slice(0, 3).map((mode) => (
          <article key={mode.label} className={`history-stat-card tone-${mode.tone || "neutral"}`}>
            <span className="history-stat-label">🧭 {mode.label}</span>
            <strong className="history-stat-value">{mode.count}</strong>
            <small className="history-stat-foot">{formatCurrency(mode.netPnL, 2, true)}</small>
          </article>
        ))}
      </section>

      <section className="history-toolbar">
        <div className="history-filter-group" role="tablist" aria-label="Filtro de historial">
          {FILTER_CONFIG.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`history-filter-btn ${filter === item.key ? "active" : ""}`}
              onClick={() => handleFilterChange(item.key)}
            >
              {item.label} ({filterCounts[item.key] || 0})
            </button>
          ))}
        </div>

        <div className="history-toolbar-right">
          <div className="rendimientos-range">
            <label>
              Desde
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                className="rendimientos-date-input"
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className="rendimientos-date-input"
              />
            </label>
            <button type="button" className="history-refresh-btn" onClick={applyLastMonth}>
              📅 Último mes
            </button>
            <button type="button" className="history-refresh-btn" onClick={clearDateRange}>
              ✖ Limpiar
            </button>
          </div>

          <button
            type="button"
            className={`history-refresh-btn ${loading ? "loading" : ""}`}
            onClick={() => fetchOrders({ showLoader: true })}
            disabled={loading}
          >
            {loading ? "⏳" : "🔄"} Recargar
          </button>
        </div>
      </section>

      {paginatedOrders.length > 0 ? (
        <>
          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Símbolo</th>
                  <th className="align-right">Cantidad</th>
                  <th className="align-right">Compra</th>
                  <th className="align-right">Máximo</th>
                  <th className="align-right">Venta</th>
                  <th className="align-right">P&L</th>
                  <th className="align-right">%</th>
                  <th className="align-center">Razón</th>
                  <th>Fecha Compra</th>
                  <th className="align-center">Detalle</th>
                </tr>
              </thead>

              <tbody>
                {viewRows.map((row) => {
                  const {
                    order,
                    rowKey,
                    isExpanded,
                    buyPrice,
                    sellPrice,
                    quantity,
                    buyValue,
                    sellValue,
                    sellNetValue,
                    sellCommission,
                    pnl,
                    pnlPercent,
                    maxPrice,
                    slPrice,
                    tpPrice,
                    slPercent,
                    tpPercent,
                    rrRatio,
                    duration,
                    maxPriceReached,
                    maxPotentialPct,
                    saleReason,
                    executionMode,
                    entryMethod,
                    marketMode,
                    rowTone,
                  } = row;
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className={`history-row ${rowTone}`}>
                        <td className="history-symbol-cell">
                          <div className="admin-users-status-cell">
                            <span className="history-symbol-title">{order?.symbol || "N/A"}</span>
                            <div className="history-symbol-badges">
                              <span className={`history-reason-badge tone-${executionMode.tone}`}>
                                {executionMode.label}
                              </span>
                              {entryMethod ? (
                                <span className={`history-reason-badge tone-${entryMethod.tone}`}>
                                  {entryMethod.label}
                                </span>
                              ) : marketMode ? (
                                <span className={`history-reason-badge tone-${marketMode.tone}`}>
                                  {marketMode.label}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="align-right mono">{formatQuantity(quantity, 6)}</td>
                        <td className="align-right">{formatCurrency(buyPrice, 4)}</td>
                        <td className="align-right muted">{formatCurrency(maxPrice, 4)}</td>
                        <td className="align-right">{formatCurrency(sellPrice, 4)}</td>
                        <td className={`align-right ${pnl > 0 ? "tone-positive" : pnl < 0 ? "tone-negative" : "tone-warning"}`}>
                          {formatCurrency(pnl, 2, true)}
                        </td>
                        <td
                          className={`align-right ${pnlPercent > 0 ? "tone-positive" : pnlPercent < 0 ? "tone-negative" : "tone-warning"}`}
                        >
                          {formatPercent(pnlPercent, 2)}
                        </td>
                        <td className="align-center">
                          <span className={`history-reason-badge tone-${saleReason.tone}`}>
                            {saleReason.label}
                          </span>
                        </td>
                        <td className="muted">{formatDate(order?.fecha_compra)}</td>
                        <td className="align-center">
                          <button
                            type="button"
                            className={`history-expand-btn ${isExpanded ? "expanded" : ""}`}
                            onClick={() =>
                              setExpandedOrderKey((prev) => (prev === rowKey ? null : rowKey))
                            }
                          >
                            {isExpanded ? "▼" : "▶"}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="history-expanded-row">
                          <td colSpan={10}>
                            <div className="history-detail-grid">
                              <article className="history-detail-card tone-negative">
                                <span className="history-detail-title">🛡️ Stop Loss</span>
                                <p>
                                  Precio: <strong>{formatCurrency(slPrice, 4)}</strong>
                                </p>
                                <p>
                                  Riesgo: <strong>{slPercent !== null ? formatPercent(-(Math.abs(slPercent)), 2) : "N/A"}</strong>
                                </p>
                              </article>

                              <article className="history-detail-card tone-positive">
                                <span className="history-detail-title">🎯 Take Profit</span>
                                <p>
                                  Precio: <strong>{formatCurrency(tpPrice, 4)}</strong>
                                </p>
                                <p>
                                  Objetivo: <strong>{tpPercent !== null ? formatPercent(tpPercent, 2) : "N/A"}</strong>
                                </p>
                              </article>

                              <article className="history-detail-card tone-warning">
                                <span className="history-detail-title">📐 R:R Ratio</span>
                                <p>
                                  Relación: <strong>{rrRatio !== null ? `${rrRatio.toFixed(2)}:1` : "N/A"}</strong>
                                </p>
                              </article>

                              <article className="history-detail-card">
                                <span className="history-detail-title">💵 Valores</span>
                                <p>
                                  Compra: <strong>{formatCurrency(buyValue, 2)}</strong>
                                </p>
                                <p>
                                  Venta bruta: <strong>{formatCurrency(sellValue, 2)}</strong>
                                </p>
                                <p>
                                  Comisión: <strong className="tone-negative">-{formatCurrency(sellCommission, 4)}</strong>
                                </p>
                                <p>
                                  Venta neta: <strong>{formatCurrency(sellNetValue ?? sellValue, 2)}</strong>
                                </p>
                                <p>
                                  Utilidad: <strong className={sellNetValue !== null && buyValue !== null && sellNetValue - buyValue >= 0 ? "tone-positive" : "tone-negative"}>{formatCurrency(sellNetValue !== null && buyValue !== null ? sellNetValue - buyValue : null, 2, true)}</strong>
                                </p>
                              </article>

                              <article className={`history-detail-card ${entryMethod ? `tone-${entryMethod.tone}` : ""}`}>
                                <span className="history-detail-title">🧭 Entrada</span>
                                <p>
                                  Método: <strong>{entryMethod?.label || "N/A"}</strong>
                                </p>
                              </article>

                              <article className={`history-detail-card ${marketMode ? `tone-${marketMode.tone}` : ""}`}>
                                <span className="history-detail-title">🧠 Modo</span>
                                <p>
                                  Motor: <strong>{marketMode?.label || "N/A"}</strong>
                                </p>
                                <p>
                                  Lectura: <strong>{marketMode?.detail || "N/A"}</strong>
                                </p>
                              </article>

                              <article className="history-detail-card">
                                <span className="history-detail-title">⏱️ Tiempo</span>
                                <p>
                                  Duración: <strong>{duration}</strong>
                                </p>
                                <p>
                                  Venta: <strong>{formatDate(order?.fecha_venta)}</strong>
                                </p>
                              </article>

                              <article className={`history-detail-card ${saleReason.tone === "neutral" ? "tone-warning" : `tone-${saleReason.tone}`}`}>
                                <span className="history-detail-title">🧾 Salida</span>
                                <p>
                                  Razón: <strong>{saleReason.label}</strong>
                                </p>
                                <p>
                                  P&L: <strong>{formatCurrency(pnl, 2, true)} ({formatPercent(pnlPercent, 2)})</strong>
                                </p>
                              </article>

                              <article className={`history-detail-card ${maxPotentialPct !== null && maxPotentialPct >= 0 ? "tone-positive" : "tone-warning"}`}>
                                <span className="history-detail-title">🚀 Potencial</span>
                                <p>
                                  Máximo: <strong>{formatCurrency(maxPriceReached, 4)}</strong>
                                </p>
                                <p>
                                  Potencial: <strong>{formatPercent(maxPotentialPct, 2)}</strong>
                                </p>
                              </article>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="history-cards">
            {viewRows.map((row) => {
              const {
                order,
                rowKey,
                isExpanded,
                buyPrice,
                sellPrice,
                quantity,
                buyValue,
                sellValue,
                sellNetValue,
                sellCommission,
                pnl,
                pnlPercent,
                maxPrice,
                slPrice,
                tpPrice,
                slPercent,
                tpPercent,
                rrRatio,
                duration,
                maxPriceReached,
                maxPotentialPct,
                saleReason,
                executionMode,
                marketMode,
                rowTone,
              } = row;

              return (
                <article key={rowKey} className={`history-card ${rowTone}`}>
                  <div className="history-card-header">
                    <div>
                      <span className="history-card-symbol">{order?.symbol || "N/A"}</span>
                      <span className="history-card-date">Compra: {formatDate(order?.fecha_compra)}</span>
                      <span className={`history-reason-badge tone-${executionMode.tone}`}>
                        {executionMode.label}
                      </span>
                      {marketMode ? (
                        <span className={`history-reason-badge tone-${marketMode.tone}`}>
                          {marketMode.label}
                        </span>
                      ) : null}
                    </div>
                    <div
                      className={`history-card-pnl ${
                        pnl > 0 ? "tone-positive" : pnl < 0 ? "tone-negative" : "tone-warning"
                      }`}
                    >
                      <span>{formatCurrency(pnl, 2, true)}</span>
                      <small>{formatPercent(pnlPercent, 2)}</small>
                    </div>
                  </div>

                  <div className="history-card-grid">
                    <div className="history-card-kv">
                      <span>Compra</span>
                      <strong>{formatCurrency(buyPrice, 4)}</strong>
                    </div>
                    <div className="history-card-kv">
                      <span>Venta</span>
                      <strong>{formatCurrency(sellPrice, 4)}</strong>
                    </div>
                    <div className="history-card-kv">
                      <span>Máximo</span>
                      <strong>{formatCurrency(maxPrice, 4)}</strong>
                    </div>
                    <div className="history-card-kv">
                      <span>Cant.</span>
                      <strong className="mono">{formatQuantity(quantity, 6)}</strong>
                    </div>
                  </div>

                  <div className="history-card-meta">
                    <span>⏱️ {duration}</span>
                    <span>🏁 {formatDate(order?.fecha_venta)}</span>
                  </div>

                  <div className="history-card-footer">
                    <span className={`history-reason-badge tone-${saleReason.tone}`}>
                      {saleReason.label}
                    </span>
                    <button
                      type="button"
                      className={`history-card-expand ${isExpanded ? "expanded" : ""}`}
                      onClick={() => setExpandedOrderKey((prev) => (prev === rowKey ? null : rowKey))}
                    >
                      {isExpanded ? "Ocultar detalle" : "Ver detalle"}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="history-card-detail">
                      <div className="history-detail-grid">
                        <article className="history-detail-card tone-negative">
                          <span className="history-detail-title">🛡️ Stop Loss</span>
                          <p>
                            Precio: <strong>{formatCurrency(slPrice, 4)}</strong>
                          </p>
                          <p>
                            Riesgo:{" "}
                            <strong>
                              {slPercent !== null ? formatPercent(-(Math.abs(slPercent)), 2) : "N/A"}
                            </strong>
                          </p>
                        </article>

                        <article className="history-detail-card tone-positive">
                          <span className="history-detail-title">🎯 Take Profit</span>
                          <p>
                            Precio: <strong>{formatCurrency(tpPrice, 4)}</strong>
                          </p>
                          <p>
                            Objetivo: <strong>{tpPercent !== null ? formatPercent(tpPercent, 2) : "N/A"}</strong>
                          </p>
                        </article>

                        <article className="history-detail-card tone-warning">
                          <span className="history-detail-title">📐 R:R Ratio</span>
                          <p>
                            Relación: <strong>{rrRatio !== null ? `${rrRatio.toFixed(2)}:1` : "N/A"}</strong>
                          </p>
                        </article>

                        <article className="history-detail-card">
                          <span className="history-detail-title">💵 Valores</span>
                          <p>
                            Compra: <strong>{formatCurrency(buyValue, 2)}</strong>
                          </p>
                          <p>
                            Venta bruta: <strong>{formatCurrency(sellValue, 2)}</strong>
                          </p>
                          <p>
                            Comisión: <strong className="tone-negative">-{formatCurrency(sellCommission, 4)}</strong>
                          </p>
                          <p>
                            Venta neta: <strong>{formatCurrency(sellNetValue ?? sellValue, 2)}</strong>
                            </p>
                            <p>
                            Utilidad: <strong className={sellNetValue !== null && buyValue !== null && sellNetValue - buyValue >= 0 ? "tone-positive" : "tone-negative"}>{formatCurrency(sellNetValue !== null && buyValue !== null ? sellNetValue - buyValue : null, 2, true)}</strong>
                            </p>
                          </article>

                        <article className={`history-detail-card ${marketMode ? `tone-${marketMode.tone}` : ""}`}>
                          <span className="history-detail-title">🧠 Modo</span>
                          <p>
                            Motor: <strong>{marketMode?.label || "N/A"}</strong>
                          </p>
                          <p>
                            Lectura: <strong>{marketMode?.detail || "N/A"}</strong>
                          </p>
                        </article>

                        <article className="history-detail-card">
                          <span className="history-detail-title">⏱️ Tiempo</span>
                          <p>
                            Duración: <strong>{duration}</strong>
                          </p>
                          <p>
                            Venta: <strong>{formatDate(order?.fecha_venta)}</strong>
                          </p>
                        </article>

                        <article
                          className={`history-detail-card ${
                            saleReason.tone === "neutral" ? "tone-warning" : `tone-${saleReason.tone}`
                          }`}
                        >
                          <span className="history-detail-title">🧾 Salida</span>
                          <p>
                            Razón: <strong>{saleReason.label}</strong>
                          </p>
                          <p>
                            P&L:{" "}
                            <strong>
                              {formatCurrency(pnl, 2, true)} ({formatPercent(pnlPercent, 2)})
                            </strong>
                          </p>
                        </article>

                        <article
                          className={`history-detail-card ${
                            maxPotentialPct !== null && maxPotentialPct >= 0
                              ? "tone-positive"
                              : "tone-warning"
                          }`}
                        >
                          <span className="history-detail-title">🚀 Potencial</span>
                          <p>
                            Máximo: <strong>{formatCurrency(maxPriceReached, 4)}</strong>
                          </p>
                          <p>
                            Potencial: <strong>{formatPercent(maxPotentialPct, 2)}</strong>
                          </p>
                        </article>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="history-pagination">
              <button
                type="button"
                className="history-page-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ⏮
              </button>
              <button
                type="button"
                className="history-page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ◀
              </button>

              <div className="history-page-window">
                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`history-page-btn ${page === currentPage ? "active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="history-page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ▶
              </button>
              <button
                type="button"
                className="history-page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                ⏭
              </button>

              <span className="history-page-info">
                Página {currentPage} de {totalPages} ({filteredOrders.length} registros)
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="history-empty">
          <p>📭 Sin órdenes para el filtro seleccionado</p>
          <button
            type="button"
            className={`history-refresh-btn ${loading ? "loading" : ""}`}
            onClick={() => fetchOrders({ showLoader: true })}
            disabled={loading}
          >
            {loading ? "⏳" : "🔄"} Recargar
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(OrderHistory);
